import { useMemo, useRef, useState } from "react";
import { TankPicker } from "@/components/drop/TankPicker";
import { SiteSwitcher } from "@/components/drop/SiteSwitcher";
import { Link } from "react-router-dom";
import { METRIC, fromShown } from "@/lib/units";
import { CornerDownLeft, Download, Printer } from "lucide-react";
import { Screen } from "@/components/kit";
import { useSession } from "@/context/SessionContext";
import { useT } from "@/lib/i18n";
import { ShortDisclaimer } from "@/components/Disclaimer";
import { endsLabel, endsShort, formatGal, productColor, productName, tankKey, TANK_PRESETS } from "@/lib/tank";
import { buildChart } from "@/lib/zones";
import { ChartSource } from "@/components/ChartSource";
import { ChartTable, ZoneLegend } from "@/components/chart/ChartTable";
import { Fingerprints } from "@/components/chart/Fingerprints";
import { BuryInput, ManifoldNote, StickBadge, ThermalCard, TiltCard, useChartTools } from "@/components/chart/FieldTools";

function exportCsv(rows, s) {
  const head = `UST chart,${s.capacity} gal,${s.diameter} in diameter,${s.ends}\n`;
  const body = "Stick_Inches,Gallons,Gal_Per_Inch,Percent_Full,Room_To_90,Inches_To_90,Zone\n" +
    rows.map((r) => [r.h, Math.round(r.vol), r.gpi.toFixed(1), r.pct.toFixed(2), Math.round(r.room90), r.to90.toFixed(3), r.zone].join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([head + body], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `UST_${s.capacity}gal_${s.diameter}in.csv` });
  a.click();
  URL.revokeObjectURL(url);
}

const Tool = ({ id, title, children }) => (
  <details className="card chart-tool" data-testid={`chart-tool-${id}`}>
    <summary data-testid={`chart-tool-${id}-toggle`}>{title}</summary>{children}
  </details>
);

export default function ChartPage() {
  const { session: s, patch, result } = useSession();
  const [jump, setJump] = useState("");
  const [tools, set] = useChartTools();
  const t = useT();
  const listRef = useRef(null);
  const isPreset = (x) => TANK_PRESETS.some((p) => tankKey(p) === tankKey(x));
  const [custom, setCustom] = useState(() => !isPreset(s));
  const chart = useMemo(() => buildChart(s.capacity, s.diameter, s.ends), [s.capacity, s.diameter, s.ends]);

  const jumpTo = () => {
    const n = fromShown(parseFloat(jump), "in");
    if (!Number.isFinite(n)) return;
    const rows = [...(listRef.current?.querySelectorAll("[data-h]") || [])];
    const row = rows.reduce((b, r) => (Math.abs(+r.dataset.h - n) < Math.abs(+b.dataset.h - n) ? r : b), rows[0]);
    if (!row) return;
    listRef.current.querySelectorAll(".row-jump").forEach((el) => el.classList.remove("row-jump"));
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    void row.offsetWidth;
    row.classList.add("row-jump");
  };

  return (
    <Screen testID="chart-screen" title={t("Calibration chart")} subtitle={t("Danger zones, ⅛\" belly rows and drop math")}>
      <div className="controls">
        <div className="jump-wrap">
          <input className="jump-input" data-testid="chart-jump-input" inputMode="decimal" placeholder={METRIC ? t("Jump to cm…") : t("Jump to inch…")} enterKeyHint="go"
            value={jump} onChange={(e) => setJump(e.target.value.replace(/[^\d.]/g, ""))} onKeyDown={(e) => e.key === "Enter" && jumpTo()} />
          <button type="button" className="jump-btn" data-testid="chart-jump-btn" onClick={jumpTo}><CornerDownLeft size={16} /></button>
        </div>
        <button type="button" className="export-btn" data-testid="chart-export-btn" onClick={() => exportCsv(chart.rows, s)}><Download size={16} />{t("Export")}</button>
        <Link to="/chart/print" className="export-btn" data-testid="chart-print-link"><Printer size={16} />{t("Print")}</Link>
      </div>
      <SiteSwitcher s={s} patch={patch} onTank={(p) => setCustom(!isPreset(p))} />
      <TankPicker s={s} patch={patch} custom={custom} setCustom={setCustom} />
      <div className="active-row">
        <span className="active-text" data-testid="chart-active">
          {t("Active")} <span className="mono">{formatGal(s.capacity)} gal · {s.diameter}" · {t(s.customDims ? endsLabel(s.ends) : endsShort(s.ends))}</span>
        </span>
        <span className="product-tag"><span className="grade-dot" style={{ background: productColor(s.product) }} />{productName(s.product)}</span>
      </div>
      <ChartSource s={s} r={result} testid="chart-page-source" />
      <StickBadge tools={tools} d={s.diameter} t={t} />
      <ZoneLegend t={t} />
      <ChartTable chart={chart} startH={s.startInches} finalH={s.dropGallons > 0 ? result.finalInches : 0} listRef={listRef} t={t} />
      <Tool id="mystery" title={t("Mystery tank fingerprints")}><Fingerprints s={s} patch={patch} t={t} /></Tool>
      <Tool id="stick" title={t("Stick length check")}><div className="tool-body"><BuryInput tools={tools} set={set} t={t} /><StickBadge tools={tools} d={s.diameter} t={t} /></div></Tool>
      <Tool id="tilt" title={t("Tank tilt correction")}><TiltCard s={s} tools={tools} set={set} t={t} /></Tool>
      <Tool id="thermal" title={t("Thermal shrink after the drop")}><ThermalCard s={s} tools={tools} set={set} t={t} /></Tool>
      <Tool id="manifold" title={t("Manifolded tanks")}><ManifoldNote t={t} /></Tool>
      <ShortDisclaimer testID="chart-disclaimer" />
    </Screen>
  );
}
